import {
  Checkbox,
  Col,
  Input,
  List,
  Row,
  Space,
  Tooltip,
  Typography,
} from "../../common/utils/antd-imports/AntdImports";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as SearchIcon } from "../../../assests/search-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import "./DmaList.css";
import {
  clearSelectedDMAs,
  setSelectedDMAs,
} from "../../../store/thunks/common-thunks/Actions";
import {
  clearSelectedState,
  clearSelectedStateId,
  setFiltersApplied,
} from "../../../store/slices/common-slice/product-filters/ProductFilter";
import { FixedSizeList as VirtualList } from "react-window";
const { Text } = Typography;

const DMAList = ({ exposeClearFunction }) => {
  const { locationList } = useSelector((state) => state.products);
  const selectedDMAs = useSelector((state) => state.dma.selectedDMAs);
  const { filterChecks, selectedSizes, searchText, productTypes } = useSelector(
    (state) => state.filters,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLetter, setSelectedLetter] = useState(null);
  const dispatch = useDispatch();
  const sectionRefs = useRef({});
  const alphabetListRef = useRef(null);
  const listRef = useRef(null);
  const letters = Array.from({ length: 26 }, (_, i) =>
    String.fromCharCode(65 + i),
  );
  const selectedSet = useMemo(
    () => new Set(selectedDMAs.map((d) => d.id)),
    [selectedDMAs],
  );
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCheckboxChange = (cityName, checked, id) => {
    let updatedLocations;

    if (checked) {
      updatedLocations = [...selectedDMAs, { id, label: cityName }];
      dispatch(setFiltersApplied(true));
      dispatch(clearSelectedState());
      dispatch(clearSelectedStateId());
    } else {
      updatedLocations = selectedDMAs?.filter(
        (location) => location?.id !== id,
      );

      // Check if any other filters are still active
      const hasOtherDmas = updatedLocations?.length > 0;
      const hasProductTypes =
        (productTypes && productTypes.length > 0) ||
        Object.values(filterChecks || {}).some(
          (arr) => Array.isArray(arr) && arr.length > 0,
        );
      const hasSizes = selectedSizes && selectedSizes.length > 0;
      const hasSearch = !!searchText?.trim();

      const hasAnyFilter =
        hasOtherDmas || hasProductTypes || hasSizes || hasSearch;

      dispatch(setFiltersApplied(hasAnyFilter));
    }
    dispatch(setSelectedDMAs(updatedLocations));
  };

  const handleLetterClick = (letter) => {
    setSelectedLetter(letter);
    const index = letterIndexMap[letter];
    if (index !== undefined && listRef.current) {
      listRef.current.scrollToItem(index, "start");
    }
  };

  const clearDmaFilters = () => {
    dispatch(clearSelectedDMAs());
  };

  const sortedLocations = useMemo(() => {
    if (!locationList) return [];
    return [...locationList]?.sort((a, b) =>
      a?.city_name?.localeCompare(b?.city_name, undefined, {
        sensitivity: "base",
      }),
    );
  }, [locationList]);
  const groupedLocations = useMemo(() => {
    const result = {};
    const searchLower = searchTerm?.toLowerCase();
    sortedLocations.forEach((location) => {
      if (!location?.city_name?.toLowerCase()?.includes(searchLower)) return;

      const firstLetter = location?.city_name[0]?.toUpperCase();
      if (!result[firstLetter]) result[firstLetter] = [];
      result[firstLetter]?.push(location);
    });

    return result;
  }, [sortedLocations, searchTerm]);

  useEffect(() => {
    if (exposeClearFunction) {
      exposeClearFunction(clearDmaFilters);
    }
  }, [exposeClearFunction]);

  // Checking the text overflow for each location name
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries?.find((e) => e.isIntersecting);
        if (visible) {
          setSelectedLetter(visible?.target.getAttribute("data-letter"));
        }
      },
      {
        root: document.querySelector(".dma-list-container"),
        rootMargin: "0px 0px -80% 0px",
        threshold: 0.1,
      },
    );

    Object.values(sectionRefs.current)?.forEach(
      (el) => el && observer.observe(el),
    );

    return () => observer?.disconnect();
  }, [groupedLocations]);

  useEffect(() => {
    if (!alphabetListRef.current || !selectedLetter) return;

    const activeItem = alphabetListRef.current.querySelector(
      `.alphabet-item.selected-alphabet`,
    );

    if (activeItem) {
      activeItem.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [selectedLetter]);

  // added the new optimised for the keep the existing ui
  const flatList = useMemo(() => {
    const list = [];

    letters.forEach((letter) => {
      const cities = groupedLocations[letter] || [];
      if (cities.length === 0) return;
      list?.push({ type: "header", letter });
      cities?.forEach((city) => {
        list?.push({ type: "city", data: city, letter });
      });
    });

    return list;
  }, [groupedLocations]);

  // Tracking the letter index value to scroll
  const letterIndexMap = useMemo(() => {
    const map = {};
    flatList?.forEach((item, index) => {
      if (item?.type === "header") {
        map[item.letter] = index;
      }
    });

    return map;
  }, [flatList]);

  const handleScroll = ({ visibleStartIndex }) => {
    const item = flatList[visibleStartIndex];
    if (item?.letter) {
      setSelectedLetter(item.letter);
    }
  };

  const RowRenderer = ({ index, style }) => {
    const item = flatList[index];
    if (item?.type === "header") {
      return (
        <div style={style} data-letter={item.letter}>
          <Text strong style={{ fontFamily: "Roboto" }}>
            {item?.letter}
          </Text>
        </div>
      );
    }

    const location = item?.data;

    return (
      <div style={style}>
        <Row style={{ padding: "4px 0" }} className="city-row">
          <Col span={4}>
            <Checkbox
              checked={selectedSet.has(location?.id)}
              onChange={(e) =>
                handleCheckboxChange(
                  location?.city_name,
                  e.target?.checked,
                  location?.id,
                )
              }
            />
          </Col>

          <Col span={19} className="dma-list-location-name">
            <div
              style={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              <span className="selected more-locations-with-count">
                {location?.city_name} - {location?.name}
              </span>{" "}
              <span className="more-locations-with-count-numbers">
                ({location?.product_count})
              </span>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div className="dma-list-popover-content">
      <Input
        prefix={<SearchIcon />}
        placeholder="Search for Locations"
        value={searchTerm}
        allowClear
        onChange={handleSearchChange}
        className="search-f-loc"
        variant="filled"
      />
      <div className="filter-options">
        <Row>
          <Col span={22}>
            <Space style={{ width: "100%" }} size={15} direction="vertical">
              <VirtualList
                ref={listRef}
                height={400}
                itemCount={flatList?.length}
                itemSize={38}
                width="100%"
                onItemsRendered={({ visibleStartIndex }) =>
                  handleScroll({ visibleStartIndex })
                }
              >
                {RowRenderer}
              </VirtualList>
            </Space>
          </Col>
          <Col span={2} style={{ display: "flex", justifyContent: "flex-end" }}>
            <div className="dma-list-alphabetics-list">
              <List
                size="small"
                dataSource={letters}
                ref={alphabetListRef}
                className="alphabet-list"
                renderItem={(item) => (
                  <List.Item onClick={() => handleLetterClick(item)}>
                    <Text
                      className={`alphabet-item ${
                        selectedLetter === item ? "selected-alphabet" : ""
                      }`}
                    >
                      {item}
                    </Text>
                  </List.Item>
                )}
              />
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DMAList;
